/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2022 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Grid Layout
 *
 * @file GridContainer.jsx
 * @author Mohamed yasar arabath M
 * @since 0.0.2
 */
import React, { forwardRef, useRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';

import StyledGridContainer from './styles/GridContainerStyles';

const GridContainer = forwardRef(({
  className, children, columns, spacing, cardWidth
}, ref) => {
  const gridContainerEle = useRef(null);
  useImperativeHandle(ref, () => ({
    gridContainer: gridContainerEle.current,
    getGridCount: () => {
      const gridComputedStyle = window.getComputedStyle(gridContainerEle.current);
      const row = gridComputedStyle.getPropertyValue('grid-template-rows').split(' ').length;
      const col = gridComputedStyle.getPropertyValue('grid-template-columns').split(' ').length;

      return { row, col };
    }
  }), []);

  return (
    <StyledGridContainer
      className={className}
      ref={gridContainerEle}
      columns={columns}
      spacing={spacing}
      cardWidth={cardWidth}
    >
      {children}
    </StyledGridContainer>
  );
});

GridContainer.defaultProps = {
  className: '',
  columns: {
    xs: 1, sm: 1, md: 2, lg: 3, xl: 3, xxl: 4
  },
  cardWidth: 'auto',
  spacing: 16
};

const gridContainer = {
  columns: {
    xs: PropTypes.number,
    sm: PropTypes.number,
    md: PropTypes.number,
    mlg: PropTypes.number,
    lg: PropTypes.number,
    xl: PropTypes.number,
    xxl: PropTypes.number
  },
  cardWidth: {
    min: PropTypes.number,
    max: PropTypes.number
  }
};

GridContainer.propTypes = {
  className: PropTypes.string,
  children: PropTypes.array.isRequired,
  columns: PropTypes.oneOfType([
    PropTypes.shape(gridContainer.columns),
    PropTypes.number
  ]),
  cardWidth: PropTypes.oneOfType([
    PropTypes.shape(gridContainer.cardWidth),
    PropTypes.number,
    PropTypes.oneOf(['auto'])
  ]),
  spacing: PropTypes.number
};

GridContainer.displayName = 'GridContainer';

export default GridContainer;
